import React, { ReactNode, useCallback, useState } from 'react';
import { Box, Menu, MenuItem, MenuList, Typography } from 'housecall-ui';
import { QUESTION_TYPE, RequestGuideNode } from 'resources/CustomerIntakeFlow/requestGuide';

import { QUESTION_TYPES } from 'components/CustomerIntakeSettings/requestGuide/consts';

export type RequestGuideNodeMenuAction = 'add' | 'edit' | 'clone' | 'up' | 'down' | 'delete';

type RequestGuideCardProps = {
  node: RequestGuideNode;
  children: (args: {
    showBranching: boolean;
    handleOptionsOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onBranchToggle: () => void;
    childrenEditable: boolean;
  }) => ReactNode;
  onMenuAction: (arg: RequestGuideNodeMenuAction) => void;
  isFirst: boolean;
  isLast: boolean;
  isHcpAssistImpersonated: boolean;
  editable: boolean;
};

export const RequestGuideCard = (props: RequestGuideCardProps) => {
  const { children, onMenuAction, isFirst, isLast, node, isHcpAssistImpersonated, editable } = props;
  const [showBranching, setBranching] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);

  const onBranchToggle = useCallback(() => {
    setMenuAnchor(null);
    setBranching((isVisible) => !isVisible);
  }, []);

  const handleOptionsOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event?.currentTarget as Element);
  }, []);

  const handleItemClick = (arg: RequestGuideNodeMenuAction) => () => {
    setMenuAnchor(null);
    onMenuAction(arg);
  };

  const childrenEditable = editable;

  const isSingleChoiceQuestion = node.type === QUESTION_TYPE && node.questionType === QUESTION_TYPES.single;

  const hasSomeQuestions = isSingleChoiceQuestion && Object.keys(node.childNodes ?? {}).length;

  const canShowAddBranch = isSingleChoiceQuestion && !hasSomeQuestions && !showBranching;

  const canBeClonable = isHcpAssistImpersonated && node.parentId;

  return (
    <Box data-testid="customerIntake__node">
      {children({
        handleOptionsOpen,
        showBranching,
        onBranchToggle,
        childrenEditable,
      })}

      <Menu
        elevation={2}
        anchorEl={menuAnchor as Element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuList>
          <MenuItem onClick={handleItemClick('edit')} data-testid="requestGuide__openEditDrawer">
            Edit
          </MenuItem>
          {canBeClonable && (
            <MenuItem onClick={handleItemClick('clone')} data-testid="requestGuide__clone">
              Duplicate
            </MenuItem>
          )}
          {canShowAddBranch && (
            <MenuItem onClick={onBranchToggle} data-testid="requestGuide__addBranch">
              Add branch
            </MenuItem>
          )}
          <MenuItem disabled={isFirst} onClick={handleItemClick('up')} data-testid="requestGuide__moveUp">
            Move up
          </MenuItem>
          <MenuItem disabled={isLast} onClick={handleItemClick('down')} data-testid="requestGuide__moveDown">
            Move down
          </MenuItem>
          <MenuItem onClick={handleItemClick('delete')} data-testid="requestGuide__delete">
            <Typography color="error">Delete</Typography>
          </MenuItem>
        </MenuList>
      </Menu>
    </Box>
  );
};
